import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { FormControl, MenuItem, Select } from "@mui/material";
import moment from "moment";
import IndexApp from "../../Components/IndexApp";
import TeachrInfoDialog, { TeachrInfoDialogHandle } from "../../Components/shared/TeachrInfoDialog";
import { getActiveBrandConfig, getActivePrestationBrandConfig, isDepositEnabled } from "../../config/environment.config";
import { isTeachrAccountActive } from "../../Utils/Function";
import { isVerifiedTrainer } from "../../Utils/VerifiedTrainerAmounts";
import { DV2 } from "../DeclarationV2/theme";
import { euros, hoursFromHalfHours, monthLabel, monthName, plural } from "../DeclarationV2/format";
import { vocab } from "../DeclarationV2/vocabulary";
import { Button, useDv2Theme } from "../DeclarationV2/layout/Ui";
import DraftsList from "../BrouillonsV2/DraftsList";
import CourseTableV2 from "./CourseTableV2";
import PastCoursesTiles from "./PastCoursesTiles";
import useCoursesSummary from "./useCoursesSummary";
import usePastPeriodStats from "./usePastPeriodStats";
import {
    ALL_PERIODS, COURSES_PERIOD_PARAM, COURSES_TAB_PARAM, COURSES_TABS, CoursesTab, DEFAULT_COURSES_TAB, TAB_COURSE_TYPE,
    TAB_FROM_LEGACY_STATE, isCoursesTab, isMonthPeriod, periodOptions,
} from "./tabs";

/**
 * « Mes cours » repensé (maquettes 6a, 6b, 6c du 1er novembre 2026) : titre et
 * sous-titre chiffré, bouton Déclarer, quatre onglets portés par l'URL
 * (`?onglet=`), tableau `CourseTableV2` ou liste des brouillons v2, note de bas
 * de page. L'onglet Passés ajoute un sélecteur de période et trois totaux.
 * L'ancien écran (`TeachrCourses`, `ListCourse`) reste dans le code.
 */
const TeachrCoursesV2 = () => {
    const teachr = useSelector((state: any) => state?.user?.currentUser?.teachr);
    const { primary, fontFamily, text } = useDv2Theme();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const infoDialogRef = useRef<TeachrInfoDialogHandle>(null);

    // Onglet : l'URL, sinon l'ancien numéro de composant transmis par l'accueil ou la fin de déclaration.
    const legacyTab = typeof location.state === "number" ? TAB_FROM_LEGACY_STATE[location.state] : undefined;
    const paramTab = searchParams.get(COURSES_TAB_PARAM);
    const tab: CoursesTab = isCoursesTab(paramTab) ? paramTab : (legacyTab ?? DEFAULT_COURSES_TAB);
    useEffect(() => {
        if (!isCoursesTab(paramTab) && legacyTab) {
            const next = new URLSearchParams(searchParams);
            next.set(COURSES_TAB_PARAM, legacyTab);
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const periodParam = searchParams.get(COURSES_PERIOD_PARAM);
    // Période des passés : toutes par défaut, un mois quand il est choisi (ou un intervalle libre dans le tableau).
    const period = periodParam && isMonthPeriod(periodParam) ? periodParam : ALL_PERIODS;

    const [currentTotal, setCurrentTotal] = useState<number | null>(null);
    const { summary } = useCoursesSummary(teachr?.id, `${tab}:${currentTotal ?? ""}`);
    const pastStats = usePastPeriodStats(teachr?.id, tab === "passes" ? period : null);

    const selectTab = (next: CoursesTab) => {
        if (next === tab) return;
        const params = new URLSearchParams(searchParams);
        params.set(COURSES_TAB_PARAM, next);
        if (next !== "passes") params.delete(COURSES_PERIOD_PARAM);
        setSearchParams(params, { replace: true });
        setCurrentTotal(null);
    };
    const selectPeriod = (value: string) => {
        const params = new URLSearchParams(searchParams);
        if (value === ALL_PERIODS) params.delete(COURSES_PERIOD_PARAM);
        else params.set(COURSES_PERIOD_PARAM, value);
        setSearchParams(params, { replace: true });
    };

    // Déclarer : mêmes garde-fous que le bouton de l'en-tête actuel (infos lues, adresse et IBAN renseignés).
    const canDeclare = !!teachr?.id && isTeachrAccountActive(teachr) && !!(teachr?.iban && teachr?.bic);
    const declare = () => {
        if (!teachr?.isShowInfos) return infoDialogRef.current?.open();
        if (!teachr?.address || !teachr?.iban) return infoDialogRef.current?.openParam();
        navigate(`/${getActiveBrandConfig()}/tableau-de-bord/${getActivePrestationBrandConfig()}/declarer`);
    };

    // ── Libellés ────────────────────────────────────────────────────────────
    const courses = vocab.courses();
    const pastPlural = `${vocab.past()}s`;
    const tabLabel: Record<CoursesTab, string> = {
        "a-venir": "À venir",
        "ce-mois": "Ce mois",
        passes: "Historique",
        brouillons: "Déclaration rapide",
    };
    const tabCount: Record<CoursesTab, number | null> = {
        "a-venir": summary.pending?.count ?? null,
        "ce-mois": summary.month?.count ?? null,
        passes: summary.past,
        brouillons: summary.drafts,
    };

    const subtitle = (): string => {
        switch (tab) {
            case "a-venir":
                return summary.pending
                    ? `${plural(summary.pending.count, vocab.course(), courses)} à venir avec ${plural(summary.pending.students, vocab.client(), vocab.clients())}, ${hoursFromHalfHours(summary.pending.halfHours)} au total`
                    : `Vos ${courses} planifiés, modifiables jusqu'à leur date de début`;
            case "ce-mois":
                return summary.month
                    ? `${monthLabel(moment())}, ${plural(summary.month.count, vocab.course(), courses)} avec ${plural(summary.month.students, vocab.client(), vocab.clients())}, ${isVerifiedTrainer() ? "prix total" : "vous recevez"} ${euros(summary.month.amount)}`
                    : `${monthLabel(moment())}, vos ${courses} du mois regroupés par ${vocab.client()}`;
            case "passes": {
                const count = isMonthPeriod(period) ? pastStats.total : summary.past;
                const when = isMonthPeriod(period) ? ` en ${monthName(moment(period, "YYYY-MM"))}` : "";
                const received = pastStats.received != null ? `, ${euros(pastStats.received)} ${isVerifiedTrainer() ? "déclarés" : "reçus"}` : "";
                return count != null ? `${plural(count, vocab.course(), courses)} ${pastPlural}${when}${received}` : `Vos ${courses} déjà dispensés${when}`;
            }
            default:
                return `Vos ${courses} de ${monthName(moment().subtract(1, "month"))} repris pour ${monthName(moment())}, à vérifier puis à déclarer`;
        }
    };

    const periodLabel = (value: string) => (value === ALL_PERIODS ? "Toutes les périodes" : monthLabel(moment(value, "YYYY-MM")));
    const selectSx = { fontFamily, fontSize: 14, fontWeight: 700, borderRadius: 12, background: "#fff", color: text, "& .MuiOutlinedInput-notchedOutline": { borderColor: DV2.border }, "& .MuiSelect-select": { py: "10px", px: "14px" } };

    const tabButton = (id: CoursesTab) => {
        const selected = id === tab;
        const count = tabCount[id];
        return (
            <button
                key={id}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => selectTab(id)}
                style={{ fontFamily, fontSize: 15, fontWeight: 800, padding: "10px 16px", background: "none", border: "none", borderBottom: `3px solid ${selected ? primary : "transparent"}`, color: selected ? primary : DV2.muted, cursor: "pointer", marginBottom: -1, whiteSpace: "nowrap" }}
            >
                {tabLabel[id]}{count != null ? `, ${count}` : ""}
            </button>
        );
    };

    const footerNote = isVerifiedTrainer()
        ? `« Vous recevez » est votre rémunération pour ${vocab.theCourse()}. Le prix total TTC payé par le client est indiqué sous le montant.`
        : `« Vous recevez » est le montant net qui vous sera versé.${isDepositEnabled() ? ` L'acompte déjà reçu de ${vocab.theClient()} est indiqué sous le montant quand il y en a un.` : ""}`;

    return (
        <IndexApp
            navBar={{ index: 1 }}
            teachr={teachr}
            component={
                <main className="px-4" style={{ background: DV2.pageBg, minHeight: "calc(100vh - 64px)", fontFamily, color: text }}>
                    <TeachrInfoDialog ref={infoDialogRef} teachr={teachr} />
                    <div className="mx-auto w-full /max-w-6xl px-4 sm:px-6 py-4 lg:py-8 flex flex-col gap-5">
                        <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                            <div className="min-w-0">
                                <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0, lineHeight: 1.2 }}>Mes {courses}</h1>
                                <div style={{ fontSize: 14, color: DV2.muted, marginTop: 4 }}>{subtitle()}</div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                {tab === "passes" && (
                                    <FormControl size="small">
                                        <Select
                                            value={period}
                                            onChange={(e) => selectPeriod(String(e.target.value))}
                                            renderValue={(value) => `Période, ${periodLabel(String(value)).toLowerCase()}`}
                                            sx={selectSx}
                                            inputProps={{ "aria-label": "Période des cours passés" }}
                                        >
                                            <MenuItem value={ALL_PERIODS} sx={{ fontFamily, fontSize: 14 }}>Toutes les périodes</MenuItem>
                                            {periodOptions().map((value) => (
                                                <MenuItem key={value} value={value} sx={{ fontFamily, fontSize: 14 }}>{periodLabel(value)}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                                {canDeclare && <Button label={`Déclarer ${vocab.aCourse()}`} onClick={declare} />}
                            </div>
                        </header>

                        <div role="tablist" aria-label={`Listes des ${courses}`} className="flex gap-1 overflow-x-auto" style={{ borderBottom: `1px solid ${DV2.border}` }}>
                            {COURSES_TABS.map(tabButton)}
                        </div>

                        {teachr?.id ? (
                            tab === "brouillons" ? (
                                <DraftsList teachr={teachr} embedded />
                            ) : (
                                <>
                                    {tab === "passes" && isMonthPeriod(period) && <PastCoursesTiles stats={pastStats} period={period} />}
                                    <CourseTableV2 key={tab} type={TAB_COURSE_TYPE[tab as Exclude<CoursesTab, "brouillons">]} period={tab === "passes" ? period : undefined} onCustomRange={() => selectPeriod(ALL_PERIODS)} onTotal={setCurrentTotal} />
                                </>
                            )
                        ) : null}

                        {tab !== "brouillons" && (
                            <p style={{ fontSize: 12, color: DV2.muted, margin: 0, lineHeight: 1.5 }}>{footerNote}</p>
                        )}
                    </div>
                </main>
            }
        />
    );
};

export default TeachrCoursesV2;
